import {
  useGetOneDepartureGroupQuery,
  useCreateDepartureGroupSegmentMutation,
  useUpdateDepartureGroupSegmentMutation,
  useDeleteDepartureGroupSegmentMutation,
} from "@/services/makkahGroups";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/CommonElements/PageTitle";
import { Button, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DatePicker, Divider, Empty, Flex, Space } from "antd";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import TableC from "@/CommonElements/TableC";
import { LabelValue } from "@/Component/Value";
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_INPUT,
  SYSTEMS,
  TIME_FORMAT,
} from "@/utils/constants";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { HugeiconsIcon } from "@hugeicons/react";
import { DeleteIcon, Edit02Icon } from "@hugeicons/core-free-icons/index";
import { Controllers } from "@/CommonElements/Controller";
import { AntInput } from "@/CommonElements/AntInput";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import dayjs from "dayjs";
import {
  filter,
  getDayNameInArabic,
  handleFormErrors,
  joinValues,
} from "@/utils/helpers";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useInfinitePrivateTransportContractsQuery } from "@/services/reports";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useSystem } from "@/utils/hooks/useLocation";
import { AddressCapacityDisplay } from "@/CommonElements/AddressCapacityDisplay";
import { AddressDate } from "@/Component/contracts/AddressDate";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { InitialGroupForm } from "@/Pages/BasicData/InitialGroup/InitialGroupForm";

export const DepartureGroupDetail = () => {
  const form = useForm();
  const { id } = useParams();
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedText } = useLocalizedText();
  const [editSegment, setEditSegment] = useState(null);
  const { confirm, ModalComponent } = useConfirm();

  const {
    mutate: deleteSegment,
    isPending: deleteSegmentPending,
  } = useDeleteDepartureGroupSegmentMutation();

  const {
    data: departureRes,
    isLoading: departureLoading,
  } = useGetOneDepartureGroupQuery({ id });
  const departureData = departureRes?.data?.data;
  const segments =
    departureData?.departure_details ?? departureData?.["departure-details"] ?? [];
  const groupData = useMemo(() => departureData?.group ?? {}, [departureData]);
  const loading = departureLoading;
  const { currentSystem } = useSystem();

  const MAKKAH_CITY_ID = 603;
  const MADINAH_CITY_ID = 605;

  const { cachedBasicData } = useCachedBasicData();
  const groupDetails = useMemo(
    () => groupData?.details ?? groupData?.group_details ?? [],
    [groupData?.details, groupData?.group_details]
  );
  const selectedContracts = useMemo(
    () =>
      cachedBasicData?.contracts?.filter((contract) =>
        groupDetails.some(
          (detail) => String(detail.contract_id) === String(contract.id)
        )
      ) ?? [],
    [groupDetails, cachedBasicData]
  );

  const selectedContractsMakkah = useMemo(
    () =>
      selectedContracts.filter(
        (c) => String(c?.city_id) === String(MAKKAH_CITY_ID)
      ),
    [selectedContracts]
  );
  const selectedContractsMadina = useMemo(
    () =>
      selectedContracts.filter(
        (c) => String(c?.city_id) === String(MADINAH_CITY_ID)
      ),
    [selectedContracts]
  );

  const totalCapacity = useMemo(
    () =>
      groupDetails.reduce(
        (acc, detail) => acc + (detail.capacity ?? 0),
        0
      ),
    [groupDetails]
  );

  useEffect(() => {
    form.reset({
      ...groupData,
      group_number: groupData?.group_number,
      supervisor_name: groupData?.supervisor_name,
      supervisor_mobile_number: groupData?.supervisor_mobile_number,
      nationality: groupData?.nationality_id,
      organizer_country: groupData?.organizer_country_id,
      delegation: groupData?.missions_id,
      affiliated_organizer: groupData?.company_id,
      service_center_id: groupData?.service_center_id,
      pilgrim_count: groupData?.pilgrim_count,
    });
  }, [groupData, form]);

  const columns = [
    { name: t("DEPARTURE_DAY"), key: "exit_day" },
    {
      name: t("DEPARTURE_DATE"),
      key: "exit_date",
      render: (value) => getLocalizedDate(value, DATE_TIME_FORMAT) || "-",
    },
    {
      name: t("DEPARTURE_DIRECTION"),
      key: "direction_id",
      render: (value) => (
        <SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="directions"
          render={(opt) => <LabelValue.Value value={opt?.name || "-"} />}
        />
      ),
    },
    {
      name: t("TRANSPORT_METHOD"),
      key: "means_id",
      render: (value) => (
        <SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="transportation"
          render={(opt) => <LabelValue.Value value={opt?.name || "-"} />}
        />
      ),
    },
    { name: t("TRANSPORT_CONTRACT"), key: "transport_contract_no" },
    {
      name: t("TRANSPORT_COMPANY"),
      key: "transport_company_name",
      render: (_, rowData) => (
        <LabelValue.Value
          value={
            getLocalizedText(
              rowData?.transport_company_name_ar,
              rowData?.transport_company_name_en
            ) || "-"
          }
        />
      ),
    },
    { name: t("BUSES_COUNT"), key: "bus_count" },
    {
      name: t("AIRLINE"),
      key: "airline_id",
      render: (value) => (
        <SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="airlines"
          render={(airline) => (
            <LabelValue.Value
              value={[
                getLocalizedText(airline?.name_ar, airline?.name_en) ||
                  airline?.name,
                airline?.code ? `(${airline.code})` : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          )}
        />
      ),
    },
    { name: t("FLIGHT_NUMBER"), key: "flight_no" },
    { name: t("FLIGHT_DAY"), key: "flight_day" },
    {
      name: t("FLIGHT_DATE"),
      key: "flight_date",
      render: (value) => getLocalizedDate(value, DATE_TIME_FORMAT) || "-",
    },
    {
      name: t("EXIT_PORT"),
      key: "port_id",
      render: (value) => (
        <SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="ports"
          render={(opt) => (
            <LabelValue.Value
              value={
                getLocalizedText(opt?.name_ar, opt?.name_en) ||
                opt?.name ||
                "-"
              }
            />
          )}
        />
      ),
    },
    { name: t("PILGRIMS_COUNT"), key: "haj_count" },
    {
      name: t("MADINAH_CONTRACTS"),
      key: "house_madina_contract_id",
      render: (value) => value ?? "-",
    },
    {
      name: t("ACTIONS"),
      key: "actions",
    },
  ];

  const getActions = (rowData) => {
    return [
      {
        label: t("EDIT"),
        key: "edit",
        onClick: () => {
          setEditSegment(rowData);
        },
        icon: <HugeiconsIcon icon={Edit02Icon} size={14} />,
      },
      {
        type: "divider",
      },
      {
        label: t("DELETE"),
        key: "delete",
        danger: true,
        onClick: () => {
          confirm({
            title: t("CONFIRM_DELETE_DEPARTURE_TITLE"),
            content: t("CONFIRM_DELETE_DEPARTURE_MESSAGE"),
            okText: t("CONFIRM"),
            cancelText: t("CANCEL"),
            onOk: ({ closeModal }) => {
              deleteSegment(
                { id: rowData.id },
                {
                  onSuccess: () => {
                    closeModal();
                  },
                }
              );
              return false;
            },
          });
        },
        icon: <HugeiconsIcon icon={DeleteIcon} size={14} />,
      },
    ];
  };

  return (
    <Stack gap={2}>
      <ModalComponent loading={deleteSegmentPending} />
      <PageHeader title={t("MAKKAH_GROUPS_TITLE")} backTo={`/app/${currentSystem}/groups-departure`} />
      <FormProvider {...form}>
        <InitialGroupForm isLoading={loading} isViewMode={true} />

        <Row className="mb-3">
          <AddressCapacityDisplay
            items={selectedContractsMakkah}
            madinahItems={selectedContractsMadina}
            isLoading={loading}
            totalCapacity={totalCapacity}
            capacityLabel={t("TOTAL_PILGRIMS")}
            addressLabel={t("HOUSING_CONTRACTS_IN_MAKKAH")}
            madinahAddressLabel={t("HOUSING_CONTRACTS_IN_MADINAH")}
            pilgrimCount={groupData?.pilgrim_count ?? 0}
            pilgrimCountLabel={t("PILGRIM_COUNT")}
            emptyMessage={t("NO_ADDRESSES_FOUND")}
            primaryItemFormatter={(contract) => {
              const { address, id } = contract;
              return [id, address].join(" - ");
            }}
            renderItem={(contract) => (
              <AddressDate contract={contract} key={contract?.id} />
            )}
          />
        </Row>
        <Divider />

        <Stack gap={2}>
          <TableC
            columnsFilter={false}
            data={segments}
            columns={columns}
            loading={departureLoading}
            actions={getActions}
          />
          {!departureLoading && (
            <Button
              className="btn-text"
              onClick={() => setEditSegment({})}
            >
              <Space>
                <i className="icon-plus"></i>
                <span>{t("GROUPS_FORM_ADD_SEGMENT_DETAILS")}</span>
              </Space>
            </Button>
          )}
        </Stack>

        <EditSegmentModal
          departure_group_id={id}
          isOpen={!!editSegment}
          segment={editSegment}
          onClose={() => setEditSegment(null)}
        />
      </FormProvider>
    </Stack>
  );
};

const EditSegmentModal = ({
  isOpen,
  onClose,
  segment,
  departure_group_id,
}) => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const isEditMode = !!Object?.values(segment || {}).length;
  const title = isEditMode ? t("EDIT_SEGMENT") : t("ADD_DEPARTURE_SEGMENT");
  const buttonText = isEditMode ? t("UPDATE") : t("SAVE");
  const { from_id, currentSystem } = useSystem();

  const {
    mutate: createSegment,
    isPending: createPending,
  } = useCreateDepartureGroupSegmentMutation();
  const {
    mutate: updateSegment,
    isPending: updatePending,
  } = useUpdateDepartureGroupSegmentMutation();
  const isPending = createPending || updatePending;
  const form = useForm({ disabled: isPending });
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = form;

  useEffect(() => {
    if (!segment) return;
    const payload = {
      ...segment,
      transport_method_id: String(segment?.transport_method_id ?? 1),
    };
    form.reset(payload ?? {});
  }, [segment, form]);

  const onSave = (data) => {
    const payload = { ...data, group_id: departure_group_id, 
      transport_company: String(data.transport_company),
      transport_contract_no: String(data.transport_contract_no),
      house_madina_contract_id: String(data.house_madina_contract_id) };
    if (isEditMode) {
      updateSegment({ ...payload, from_id },
        {
          onSuccess: () => {
            onClose();
          },
          onError: (error) => {
            handleFormErrors(error, form.setError, t, "DEPARTURE_UPDATE_FAILED");
          },
        }
      );
    } else {
      createSegment(
        { ...payload, from_id },
        {
          onSuccess: () => {
            onClose();
          },
          onError: (error) => {
            handleFormErrors(
              error,
              form.setError,
              t,
              "DEPARTURE_CREATION_FAILED"
            );
          },
        }
      );
    }
  };

  const ctrls = [
    {
      labelProps: { children: t("EXIT_DATE"), className: "required" },
      controllerProps: {
        name: "exit_date",
        rules: { required: t("FIELD_REQUIRED") },
        render: ({ field }) => (
          <DatePicker
            {...field}
            size="large"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) => {
              field.onChange(date);
              const dayName = getDayNameInArabic(date);
              form.setValue("exit_day", dayName);
            }}
            id="exit_date"
            className="w-100"
            placeholder={t("EXIT_DATE")}
            status={errors.departure_date ? "error" : undefined}
            format={DATE_TIME_FORMAT_INPUT}
            showTime={{ format: TIME_FORMAT, minuteStep: 1 }}
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("DEPARTURE_DAY") },
      controllerProps: { name: "exit_day" },
      isViewMode: true,
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("DEPARTURE_DIRECTION"),
        className: "required",
        for: "direction_id",
      },
      controllerProps: {
        name: "direction_id",
        rules: { required: t("FIELD_REQUIRED") },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            id="direction_id"
            placeholder={t("DEPARTURE_DIRECTION")}
            status={errors.direction_id ? "error" : undefined}
            optionKey="directions"
            options={(d) => ({
              value: String(d.id),
              label: <LabelValue.Value value={d.name} />,
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("TRANSPORT_METHOD"),
        className: "required",
        for: "means_id",
      },
      controllerProps: {
        name: "means_id",
        rules: { required: t("FIELD_REQUIRED") },
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            id="means_id"
            showSearch
            placeholder={t("BUSES_COUNT")}
            status={errors.means_id ? "error" : undefined}
            optionKey="transportation"
            options={(transportMethod) => ({
              value: String(transportMethod.id),
              label: (
                <LabelValue.Value value={transportMethod.name} />
              ),
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("TRANSPORT_CONTRACT"),
        for: "transport_contract_no",
      },
      controllerProps: {
        name: "transport_contract_no",
        render: ({ field: { onChange, ...field } }) => (
          <SelectPrivateTransportContracts
            {...field}
            valueAsObject={
              segment?.transport_contract_no
                ? {
                    id: segment?.transport_contract_no,
                    provider: {
                      name_ar: segment?.transport_company_name_ar,
                    },
                    name_en: segment?.transport_company_name_en,
                  }
                : null
            }
            onChange={({ value, contract }) => {
              onChange(value);
              form.setValue(
                "transport_company",
                contract.provider_entity_id
              );
            }}
            id="transport_contract_no"
            placeholder={t("TRANSPORT_CONTRACT")}
            status={
              errors.transport_contract_no ? "error" : undefined
            }
          />
        ),
      },
    },
    {
      labelProps: { children: t("BUSES_COUNT"), for: "bus_count" },
      controllerProps: {
        name: "bus_count",
        rules: {
          validate: (value) => {
            if (!value) return true;
            if (!/^\d+$/.test(value))
              return t("MUST_BE_NUMBERS_DIGITS", {
                defaultValue: "Must be numbers/digits",
              });
            if (value.length > 2)
              return t("CAMPAIGNS_FORM_MAX_TWO_DIGITS", {
                defaultValue: t("MAX_TWO_DIGITS", {
                  defaultValue: "Max 2 digits",
                }),
              });
            return true;
          },
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="bus_count"
            type="text"
            placeholder={t("BUSES_COUNT")}
            status={errors.buses_count ? "error" : undefined}
            maxLength={2}
          />
        ),
      },
    },
    {
      labelProps: { children: t("AIRLINE"), for: "airline_id" },
      controllerProps: {
        name: "airline_id",
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            id="airline_id"
            placeholder={t("AIRLINE")}
            status={errors.airline_id ? "error" : undefined}
            optionKey="airlines"
            options={(a) => ({
              value: String(a.id),
              label: (
                <LabelValue.Value
                  value={joinValues(
                    filter([
                      getLocalizedText(a?.name_ar, a?.name_en) ||
                        a?.name,
                      a?.code ? `(${a.code})` : "",
                    ]),
                    " "
                  )}
                />
              ),
            })}
          />
        ),
      },
    },
    {
      labelProps: { children: t("FLIGHT_NUMBER") },
      controllerProps: {
        name: "flight_no",
        rules: {
          maxLength: {
            value: 4,
            message: t("MAX_LENGTH", { max: 4 }),
          },
          pattern: {
            value: /^[0-9]+$/,
            message: t("FLIGHT_NUMBER_NUMERIC_ONLY", {
              defaultValue:
                "Flight number must contain only numbers",
            }),
          },
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            id="flight_no"
            type="text"
            placeholder={t("FLIGHT_NUMBER")}
            status={errors.flight_no ? "error" : undefined}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("FLIGHT_DATE"),
        for: "flight_date",
      },
      controllerProps: {
        name: "flight_date",
        render: ({ field }) => (
          <DatePicker
            {...field}
            size="large"
            value={field.value ? dayjs(field.value) : null}
            onChange={(date) => {
              field.onChange(date);
              const dayName = getDayNameInArabic(date);
              form.setValue("flight_day", dayName);
            }}
            id="flight_date"
            className="w-100"
            placeholder={t("FLIGHT_DATE")}
            status={errors.flight_date ? "error" : undefined}
            format={DATE_TIME_FORMAT_INPUT}
            showTime={{ format: TIME_FORMAT, minuteStep: 1 }}
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("FLIGHT_DAY"),
        for: "flight_day",
      },
      controllerProps: { name: "flight_day" },
      isViewMode: true,
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("EXIT_PORT"),
        for: "port_id",
      },
      controllerProps: {
        name: "port_id",
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            id="port_id"
            placeholder={t("EXIT_PORT")}
            status={errors.port_id ? "error" : undefined}
            optionKey="ports"
            options={(p) => ({
              value: String(p.id),
              label: (
                <LabelValue.Value
                  value={
                    getLocalizedText(p?.name_ar, p?.name_en) ||
                    p?.name
                  }
                />
              ),
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children:
          currentSystem === SYSTEMS.madina
            ? t("MAKKAH_CONTRACTS")
            : t("MADINAH_CONTRACTS"),
        for: "house_madina_contract_id",
      },
      controllerProps: {
        name: "house_madina_contract_id",
        render: ({ field }) => (
          <SelectCachedOptions
            {...field}
            showSearch
            id="house_madina_contract_id"
            placeholder={t("MADINAH_CONTRACTS")}
            status={
              errors.house_madina_contract_id ? "error" : undefined
            }
            optionKey="madinahContracts"
            options={(c) => ({
              value: String(c.id),
              label: (
                <LabelValue.Value
                  value={
                    c.house_commercial_name_ar
                      ? `${c.id} - ${c.house_commercial_name_ar}`
                      : String(c.id)
                  }
                />
              ),
            })}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("PILGRIMS_COUNT"),
        for: "haj_count",
      },
      controllerProps: {
        name: "haj_count",
        render: ({ field }) => (
          <AntInput
            {...field}
            showSearch
            id="haj_count"
            placeholder={t("PILGRIMS_COUNT")}
            status={errors.haj_count ? "error" : undefined}
          />
        ),
      },
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader toggle={onClose} className="sticky-top bg-white">
        <span className="fw-bold H5">{title}</span>
      </ModalHeader>
      <form onSubmit={handleSubmit(onSave)}>
        <ModalBody>
          <Controllers
            controllers={ctrls.map((c) => ({
              ...c,
              name: c.controllerProps.name,
              controllerProps: {
                ...c.controllerProps,
                control,
                errors,
              },
            }))}
            watch={watch}
          />
          <Controller
            control={control}
            name="transport_company"
            render={({ field }) => (
              <AntInput {...field} style={{ display: "none" }} />
            )}
          />
        </ModalBody>
        <ModalFooter className="sticky-bottom bg-white d-flex justify-content-start">
          <Button
            disabled={isPending}
            className="btn-sm"
            type="submit"
            variant="primary"
          >
            {isPending ? <Spinner size="sm" /> : buttonText}
          </Button>
          <Button
            disabled={isPending}
            style={{
              pointerEvents: isPending ? "none" : "auto",
            }}
            className="btn-sm"
            type="button"
            variant="danger"
            onClick={onClose}
          >
            {t("CANCEL")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

const SelectPrivateTransportContracts = ({
  onChange,
  valueAsObject = null,
  ...props
}) => {
  const { getLocalizedText } = useLocalizedText();
  const { debouncedValue, setSearchValue } = useDebouncedSearch("", 500);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfinitePrivateTransportContractsQuery({
    search: debouncedValue,
  });

  const contracts =
    data?.pages.flatMap((page) => page.data.data) ?? [];

  const handlePopupScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <AntSelect
      {...props}
      showSearch
      onSearch={setSearchValue}
      onPopupScroll={handlePopupScroll}
      filterOption={false}
      onChange={(value) => {
        const contract = contracts.find(
          (c) => String(c.id) === String(value)
        );
        onChange({ value, contract });
      }}
      options={[
        valueAsObject,
        ...contracts.filter((c) => c.id !== valueAsObject?.id),
      ]
        ?.filter?.(Boolean)
        ?.map((contract) => ({
          value: contract?.id,
          label: joinValues([
            contract?.id,
            getLocalizedText(
              contract?.provider?.name_ar,
              contract?.provider?.name_en
            ),
          ]),
        }))}
      notFoundContent={
        isLoading ? (
          <Flex align="center" justify="center">
            <Spinner size="sm" />
          </Flex>
        ) : (
          <Empty />
        )
      }
      dropdownRender={(menu) => (
        <>
          {menu}
          {isFetchingNextPage && (
            <div style={{ textAlign: "center", padding: "8px" }}>
              <Spinner size="sm" />
            </div>
          )}
        </>
      )}
    />
  );
};
